import React, { useState, useEffect } from 'react';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  TablePagination,
  Typography,
  TableSortLabel
} from '@material-ui/core';
import { formatDateAndTime } from "../../../utilities";
import _ from 'lodash';
import "./CommentsPanel"

const DEFAULT_ROWS_PER_PAGE = 25;
const mapStateToProps = state => ({
  resolutionSummary: _.get(state, 'comments.fetchAllResolutionSummary', []),
  countResolutionSummaryForProject: _.get(state, 'comments.fetchCommentCountByResolutionSummary', 0),
  loading: _.get(state, 'comments.request.loading', false),
});

export const ResolutionSummaryModal = ({
  open,
  onClose,
  resolutionSummary,
  fetchResolutionSummary,
  categoryId,
  commentSeqNbr,
  countResolutionSummaryForProject,
}) => {
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(DEFAULT_ROWS_PER_PAGE);
  const [sortField, setSortField] = useState('');
  const [sortDirection, setSortDirection] = useState('DESC');

  useEffect(() => {
    if (open) {
      setPage(0);
      setRowsPerPage(DEFAULT_ROWS_PER_PAGE);
      setSortField('');
      setSortDirection('DESC');
    }
  }, [open]);

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
    fetchResolutionSummary(categoryId, commentSeqNbr, newPage * rowsPerPage, rowsPerPage, sortField, sortDirection);
  };


  const handleChangeRowsPerPage = (event) => {
    const rawValue = event?.target?.value;
    const parsedValue = Number.parseInt(rawValue, 10);
    if (!Number.isInteger(parsedValue) || parsedValue <= 0) {
      setRowsPerPage(DEFAULT_ROWS_PER_PAGE);
      setPage(0);
      fetchResolutionSummary(categoryId, commentSeqNbr, 0, DEFAULT_ROWS_PER_PAGE, sortField, sortDirection);
      return;
    }
    setRowsPerPage(parsedValue);
    setPage(0);
    fetchResolutionSummary(categoryId, commentSeqNbr, 0, parsedValue, sortField, sortDirection);
  };

  const handleSort = (field) => {
    const newDirection = sortField === field && sortDirection === 'ASC' ? 'DESC' : 'ASC';
    setSortField(field);
    setSortDirection(newDirection);
    setPage(0);
    fetchResolutionSummary(categoryId, commentSeqNbr, 0, rowsPerPage, field, newDirection);
  };

  // When total count is unknown, estimate: if a full page is returned, assume at least one more page exists
  const estimatedCount = resolutionSummary.length < rowsPerPage
    ? page * rowsPerPage + resolutionSummary.length
    : (page + 2) * rowsPerPage;


  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>Resolution Summary</DialogTitle>
      <DialogContent>
        {resolutionSummary.length === 0 && page === 0 && (
          <Typography>No resolution summary available.</Typography>
        )}
        {resolutionSummary.length > 0 && (
          <Table>
            <TableHead>
              <TableRow>
                <TableCell className='font-color-white'>Sr No.</TableCell>
                <TableCell>
                  <TableSortLabel
                    className='font-color-white'
                    active={sortField === 'extrn_first_name'}
                    direction={sortField === 'extrn_first_name' ? sortDirection.toLowerCase() : 'asc'}
                    onClick={() => handleSort('extrn_first_name')}
                  >Updated By</TableSortLabel></TableCell>
                <TableCell>
                  <TableSortLabel
                    className='font-color-white'
                    active={sortField === 'action_type'}
                    direction={sortField === 'action_type' ? sortDirection.toLowerCase() : 'asc'}
                    onClick={() => handleSort('action_type')}
                  >Action Type</TableSortLabel></TableCell>
                <TableCell>
                  <TableSortLabel
                    className='font-color-white'
                    active={sortField === 'action_ts'}
                    direction={sortField === 'action_ts' ? sortDirection.toLowerCase() : 'asc'}
                    onClick={() => handleSort('action_ts')}
                  >Updated Date</TableSortLabel>
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {resolutionSummary.map((row, index) => (
                <TableRow key={index}>
                  <TableCell>{page * rowsPerPage + index + 1}</TableCell>
                  <TableCell>{row.user_full_name}</TableCell>
                  <TableCell>{_.capitalize(row.action_type)}</TableCell>
                  <TableCell>{row.updated_date ? formatDateAndTime(row.updated_date) : ''}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
        {(resolutionSummary.length > 0 || page > 0) && (
          <TablePagination
            component="div"
            count={countResolutionSummaryForProject || estimatedCount}
            page={page}
            rowsPerPage={rowsPerPage}
            rowsPerPageOptions={[25, 50, 100]}
            onChangePage={handleChangePage}
            onChangeRowsPerPage={handleChangeRowsPerPage}
          />
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} color="primary">
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
};

ResolutionSummaryModal.defaultProps = {
  resolutionSummary: [],
  countResolutionSummaryForProject: 0,
};

ResolutionSummaryModal.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  resolutionSummary: PropTypes.array,
  fetchResolutionSummary: PropTypes.func,
  categoryId: PropTypes.number,
  commentSeqNbr: PropTypes.number,
  countResolutionSummaryForProject: PropTypes.number,
};

export default connect(mapStateToProps)(ResolutionSummaryModal);
